/**
 * Session Warning Modal - Warns users when their session is about to expire
 *
 * Features:
 * - Shows countdown timer
 * - Prominent "Stay Logged In" button
 * - Option to dismiss (warning will reappear if still inactive)
 * - Clear messaging about what's happening
 */

import styled from '@emotion/styled';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Backdrop, Button, Dialog, LinearProgress } from '@mui/material';
import React from 'react';

import Modal from '../base/modal';

import { useSessionContextOptional } from '~/client/contexts/session-context';
import Arranger from '~c/components/base/arranger';
import { DesignToken } from '~c/components/base/core';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

const WarningIcon = styled(WarningAmberIcon)`
  font-size: 68px;
  color: ${DesignToken.status.warning};
`;

const CountdownText = styled(Text)`
  font-size: 2rem;
  font-weight: bold;
  color: ${DesignToken.status.warning};
`;

export const SessionWarningModal: React.FC = () => {
  const session = useSessionContextOptional();

  // Don't render if not in session context
  if (!session) {
    return null;
  }

  const { isWarningVisible, secondsRemaining, extendSession, dismissWarning } =
    session;

  if (!isWarningVisible) {
    return null;
  }

  // Format time as MM:SS
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const handleExtendSession = async () => {
    await extendSession();
  };

  return (
    <Modal
      slots={{ backdrop: Backdrop }}
      style={{ width: '1000px', marginLeft: 'auto', marginRight: 'auto' }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        },
      }}
      open={isWarningVisible}
      onClose={dismissWarning}
      title="Session Expiring Soon"
    >
      <Arranger direction="column" justify="flex-start" alignItems="center">
        <WarningIcon />
        <Spacer pb={spc.m} />

        <Text sz="body16" textAlign="center" color={DesignToken.textMuted}>
          Your session will expire due to inactivity.
        </Text>
        <Spacer pb={spc.xs} />

        <CountdownText>{timeDisplay}</CountdownText>
        <Spacer pb={spc.s} />

        <Arranger direction="row" justify="center" style={{ gap: '16px' }}>
          <Button
            variant="contained"
            onClick={handleExtendSession}
            sx={{
              backgroundColor: DesignToken.brand.primary,
              color: DesignToken.brand.primaryContrast,
            }}
            autoFocus
            size="large"
          >
            Continue Working
          </Button>
        </Arranger>
        <Spacer pb={spc.l} />
      </Arranger>
    </Modal>
  );
};

export default SessionWarningModal;
